import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import type { Dispatch, ReactNode } from "react";
import { getSimulationSnapshot } from "./geometry";
import { createInitialSimulationState, simulationReducer } from "./state";
import type {
  SimulationAction,
  SimulationGuides,
  SimulationState,
} from "./state";
import type { SimulationSnapshot } from "./types";

export interface SimulationContextValue {
  readonly state: SimulationState;
  readonly snapshot: SimulationSnapshot;
  readonly dispatch: Dispatch<SimulationAction>;
  readonly togglePlayback: () => void;
  readonly setSpeed: (speed: SimulationState["speed"]) => void;
  readonly setDay: (day: number) => void;
  readonly selectPhase: (
    phaseIndex: number,
    direction: "shortest" | "forward" | "backward",
  ) => void;
  readonly toggleGuide: (guide: keyof SimulationGuides) => void;
  readonly resetView: () => void;
}

const SimulationContext = createContext<SimulationContextValue | null>(null);

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
}

export function SimulationProvider({
  children,
  initialDay = null,
}: {
  readonly children: ReactNode;
  // A shared lesson link (#/demos/moon-phases?day=14.8) seeds the first
  // render directly, so the shareable-URL write effect can never clobber
  // the incoming link with default state under StrictMode.
  readonly initialDay?: number | null;
}) {
  const [state, dispatch] = useReducer(simulationReducer, undefined, () =>
    createInitialSimulationState(initialDay),
  );
  const animationFrameId = useRef<number | null>(null);
  const previousFrameTime = useRef<number | null>(null);
  const snapshot = useMemo(() => getSimulationSnapshot(state.day), [state.day]);
  const isAnimating = state.isPlaying || state.transition !== null;

  useEffect(() => {
    if (!isAnimating) {
      previousFrameTime.current = null;
      return;
    }

    const onAnimationFrame = (timestamp: number) => {
      const previousTimestamp = previousFrameTime.current;
      previousFrameTime.current = timestamp;

      if (previousTimestamp !== null) {
        const elapsedSeconds = (timestamp - previousTimestamp) / 1_000;
        if (elapsedSeconds <= 0.25) {
          dispatch({ type: "tick", elapsedSeconds });
        }
      }

      animationFrameId.current = requestAnimationFrame(onAnimationFrame);
    };

    animationFrameId.current = requestAnimationFrame(onAnimationFrame);

    return () => {
      if (animationFrameId.current !== null) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
      previousFrameTime.current = null;
    };
  }, [isAnimating]);

  const togglePlayback = useCallback(() => {
    dispatch({ type: "togglePlayback" });
  }, []);
  const setSpeed = useCallback((speed: SimulationState["speed"]) => {
    dispatch({ type: "setSpeed", speed });
  }, []);
  const setDay = useCallback((day: number) => {
    dispatch({ type: "setDay", day });
  }, []);
  const selectPhase = useCallback(
    (phaseIndex: number, direction: "shortest" | "forward" | "backward") => {
      dispatch({
        type: "selectPhase",
        phaseIndex,
        direction,
        reduceMotion: prefersReducedMotion(),
      });
    },
    [],
  );
  const toggleGuide = useCallback((guide: keyof SimulationGuides) => {
    dispatch({ type: "toggleGuide", guide });
  }, []);
  const resetView = useCallback(() => {
    dispatch({ type: "resetView" });
  }, []);

  const value = useMemo<SimulationContextValue>(
    () => ({
      state,
      snapshot,
      dispatch,
      togglePlayback,
      setSpeed,
      setDay,
      selectPhase,
      toggleGuide,
      resetView,
    }),
    [
      state,
      snapshot,
      togglePlayback,
      setSpeed,
      setDay,
      selectPhase,
      toggleGuide,
      resetView,
    ],
  );

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
}

// The hook belongs beside its context so consumers cannot access the context directly.
// eslint-disable-next-line react-refresh/only-export-components
export function useSimulation(): SimulationContextValue {
  const context = useContext(SimulationContext);

  if (context === null) {
    throw new Error("useSimulation must be used within a SimulationProvider");
  }

  return context;
}
